/**
 * Elements shared by the design editor and the video editor: charts, tables, device mockups and
 * decorative graphics. Each is built as Fabric objects at a fixed base size; the design editor adds
 * them to the page (charts and tables keep their data so they can be edited later), the video
 * editor renders them to a PNG picture layer with renderToBlob().
 */
import {
  Circle, FabricImage, FabricObject, Group, Line, Path, Polygon, Rect, Shadow, StaticCanvas, Textbox,
} from 'fabric';
import { Frame, fitImageToFrame, makePlaceholder, newId } from './fabricKit';

export const ELEMENT_COLORS = ['#0b7a5a', '#25d366', '#f59e0b', '#e11d48', '#6366f1', '#0ea5e9', '#a855f7', '#14b8a6'];

const INK = '#10231e';
const MUTED = '#5b6f68';
const GRID = '#e1e9e5';

const text = (t: string, opts: Partial<Textbox> & Record<string, unknown>): Textbox =>
  new Textbox(t, { fontFamily: 'Poppins', fill: INK, fontSize: 18, ...opts });

/** Groups objects drawn at absolute base coordinates, with an id and a name. */
function group(objects: FabricObject[], name: string, extra: Record<string, unknown> = {}): Group {
  return new Group(objects, { id: newId(), name, subTargetCheck: false, ...extra });
}

/** Scales and centres an object inside a page (at most `share` of the page). */
export function placeOnPage(o: FabricObject, W: number, H: number, share = 0.6): FabricObject {
  const w = o.width * (o.scaleX || 1);
  const h = o.height * (o.scaleY || 1);
  const k = Math.min((W * share) / w, (H * share) / h);
  o.set({ scaleX: (o.scaleX || 1) * k, scaleY: (o.scaleY || 1) * k });
  o.set({ left: (W - w * k) / 2, top: (H - h * k) / 2 });
  o.setCoords();
  return o;
}

/** Objects as a tightly cropped transparent PNG, `width` pixels wide. */
export async function renderToBlob(objects: FabricObject[], width = 900): Promise<Blob> {
  const g = objects.length === 1 ? objects[0] : new Group(objects);
  const w = g.width * (g.scaleX || 1);
  const h = g.height * (g.scaleY || 1);
  const k = width / w;
  const el = document.createElement('canvas');
  const c = new StaticCanvas(el, {
    width: Math.ceil(w * k), height: Math.ceil(h * k), enableRetinaScaling: false, renderOnAddRemove: false,
  });
  g.set({ left: 0, top: 0, scaleX: (g.scaleX || 1) * k, scaleY: (g.scaleY || 1) * k, angle: 0 });
  g.setCoords();
  c.add(g);
  c.renderAll();
  const url = c.toDataURL({ format: 'png', multiplier: 1 });
  c.dispose();
  return (await fetch(url)).blob();
}

// ================================================================ charts

export type ChartKind = 'column' | 'bar' | 'line' | 'pie' | 'donut';

export interface ChartSpec {
  kind: ChartKind;
  title: string;
  labels: string[];
  values: number[];
  colors: string[];
  /** A white rounded card behind the chart (reads well on photos and video). */
  card: boolean;
}

export const CHART_KINDS: ReadonlyArray<readonly [ChartKind, string]> = [
  ['column', 'Columns'], ['bar', 'Bars'], ['line', 'Line'], ['pie', 'Pie'], ['donut', 'Donut'],
];

export const sampleChart = (kind: ChartKind = 'column', colors = ELEMENT_COLORS): ChartSpec => ({
  kind,
  title: 'Monthly sales',
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
  values: [12, 19, 15, 24, 30],
  colors,
  card: true,
});

/** "Label, 12" / "Label: 12" / "Label<tab>12" per line → labels and values (at most 12 rows). */
export function parseChartRows(src: string): { labels: string[]; values: number[] } {
  const labels: string[] = [];
  const values: number[] = [];
  src.split(/\r?\n/).forEach((line) => {
    const m = line.trim().match(/^(.*?)[\s,:;\t]+(-?[\d.,]+)\s*%?$/);
    if (!m) return;
    const v = Number(m[2].replace(/,/g, ''));
    if (!Number.isFinite(v)) return;
    labels.push(m[1].trim() || `Item ${labels.length + 1}`);
    values.push(v);
  });
  return { labels: labels.slice(0, 12), values: values.slice(0, 12) };
}

export const chartRowsText = (s: ChartSpec): string => s.labels.map((l, i) => `${l}, ${s.values[i]}`).join('\n');

const fmtNum = (v: number): string =>
  Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v * 10) / 10}`;

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const e = 10 ** Math.floor(Math.log10(max));
  const f = max / e;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * e;
}

/** A pie / donut slice from angle a0 to a1 (radians, 0 = 12 o'clock). r0 > 0 makes a ring slice. */
function slice(cx: number, cy: number, r: number, r0: number, a0: number, a1: number): string {
  const pt = (rad: number, a: number) => `${cx + rad * Math.sin(a)} ${cy - rad * Math.cos(a)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  if (r0 <= 0) return `M ${cx} ${cy} L ${pt(r, a0)} A ${r} ${r} 0 ${large} 1 ${pt(r, a1)} Z`;
  return `M ${pt(r, a0)} A ${r} ${r} 0 ${large} 1 ${pt(r, a1)} L ${pt(r0, a1)} A ${r0} ${r0} 0 ${large} 0 ${pt(r0, a0)} Z`;
}

/** The chart as one group (base size 800 × 500), carrying its spec in `chart`. */
export function makeChart(spec: ChartSpec): Group {
  const W = 800;
  const H = 500;
  const pad = 32;
  const n = Math.max(1, spec.values.length);
  const color = (i: number) => spec.colors[i % spec.colors.length] || ELEMENT_COLORS[i % ELEMENT_COLORS.length];
  const o: FabricObject[] = [];
  if (spec.card) {
    o.push(new Rect({
      left: 0, top: 0, width: W, height: H, rx: 24, ry: 24, fill: '#ffffff',
      shadow: new Shadow({ color: 'rgba(16,35,30,0.16)', blur: 24, offsetY: 8 }),
    }));
  }
  const titleH = spec.title.trim() ? 58 : 0;
  if (titleH) {
    o.push(text(spec.title.trim(), { left: pad, top: pad - 4, width: W - pad * 2, fontSize: 30, fontWeight: 700 }));
  }
  const values = spec.values.length ? spec.values : [0];
  const labels = spec.labels.length ? spec.labels : [''];

  if (spec.kind === 'pie' || spec.kind === 'donut') {
    const sum = values.reduce((a, v) => a + Math.max(0, v), 0) || 1;
    const r = Math.min(H - pad * 2 - titleH, W * 0.5) / 2;
    const cx = pad + r + 10;
    const cy = pad + titleH + (H - pad * 2 - titleH) / 2;
    const r0 = spec.kind === 'donut' ? r * 0.58 : 0;
    let a = 0;
    values.forEach((v, i) => {
      const share = Math.max(0, v) / sum;
      if (share <= 0) return;
      if (share > 0.9999) {
        // a whole circle: two halves (an arc can't start and end at the same point)
        o.push(new Path(slice(cx, cy, r, r0, 0, Math.PI), { fill: color(i) }));
        o.push(new Path(slice(cx, cy, r, r0, Math.PI, Math.PI * 2), { fill: color(i) }));
      } else {
        o.push(new Path(slice(cx, cy, r, r0, a, a + share * Math.PI * 2), { fill: color(i), stroke: '#ffffff', strokeWidth: 3 }));
      }
      a += share * Math.PI * 2;
    });
    if (spec.kind === 'donut') {
      o.push(text(fmtNum(sum), {
        left: cx - r0, top: cy - 24, width: r0 * 2, fontSize: 34, fontWeight: 700, textAlign: 'center',
      }));
    }
    const lx = cx + r + 48;
    const rowH = Math.min(44, (H - pad * 2 - titleH) / values.length);
    const ly = cy - (rowH * values.length) / 2;
    values.forEach((v, i) => {
      const y = ly + i * rowH;
      o.push(new Rect({ left: lx, top: y + rowH / 2 - 9, width: 18, height: 18, rx: 5, ry: 5, fill: color(i) }));
      o.push(text(`${labels[i] ?? ''}  ${Math.round((Math.max(0, v) / sum) * 100)}%`, {
        left: lx + 30, top: y + rowH / 2 - 13, width: W - lx - 30 - pad, fontSize: Math.min(22, rowH * 0.55),
      }));
    });
    return group(o, 'Chart', { chart: spec });
  }

  const max = niceMax(Math.max(...values, 0));
  if (spec.kind === 'bar') {
    const labelW = 150;
    const x0 = pad + labelW;
    const x1 = W - pad - 70;
    const y0 = pad + titleH;
    const y1 = H - pad;
    const slot = (y1 - y0) / n;
    const bh = slot * 0.62;
    values.forEach((v, i) => {
      const y = y0 + slot * i + (slot - bh) / 2;
      const w = Math.max(2, ((x1 - x0) * Math.max(0, v)) / max);
      o.push(text(labels[i] ?? '', {
        left: pad, top: y + bh / 2 - 12, width: labelW - 14, fontSize: 19, textAlign: 'right', fill: MUTED,
      }));
      o.push(new Rect({ left: x0, top: y, width: w, height: bh, rx: 6, ry: 6, fill: color(0) }));
      o.push(text(fmtNum(v), { left: x0 + w + 10, top: y + bh / 2 - 12, width: 70, fontSize: 19, fontWeight: 600 }));
    });
    return group(o, 'Chart', { chart: spec });
  }

  // column and line share the axes
  const axisW = 58;
  const labelH = 40;
  const x0 = pad + axisW;
  const x1 = W - pad;
  const y0 = pad + titleH + 14;
  const y1 = H - pad - labelH;
  for (let k = 0; k <= 4; k++) {
    const y = y1 - ((y1 - y0) * k) / 4;
    o.push(new Line([x0, y, x1, y], { stroke: k === 0 ? '#b9c9c2' : GRID, strokeWidth: 2 }));
    o.push(text(fmtNum((max * k) / 4), {
      left: pad - 6, top: y - 11, width: axisW - 8, fontSize: 16, textAlign: 'right', fill: MUTED,
    }));
  }
  const slot = (x1 - x0) / n;
  const yOf = (v: number) => y1 - ((y1 - y0) * Math.max(0, v)) / max;
  labels.forEach((l, i) => {
    o.push(text(l, { left: x0 + slot * i, top: y1 + 10, width: slot, fontSize: 18, textAlign: 'center', fill: MUTED }));
  });
  if (spec.kind === 'line') {
    const pts = values.map((v, i) => [x0 + slot * (i + 0.5), yOf(v)] as const);
    const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'} ${x} ${y}`).join(' ');
    o.push(new Path(`${d} L ${pts[pts.length - 1][0]} ${y1} L ${pts[0][0]} ${y1} Z`, { fill: color(0), opacity: 0.12 }));
    o.push(new Path(d, { fill: '', stroke: color(0), strokeWidth: 5, strokeLineJoin: 'round', strokeLineCap: 'round' }));
    pts.forEach(([x, y], i) => {
      o.push(new Circle({ left: x - 8, top: y - 8, radius: 8, fill: '#ffffff', stroke: color(0), strokeWidth: 4 }));
      o.push(text(fmtNum(values[i]), { left: x - 40, top: y - 36, width: 80, fontSize: 17, fontWeight: 600, textAlign: 'center' }));
    });
  } else {
    const bw = slot * 0.58;
    values.forEach((v, i) => {
      const top = yOf(v);
      const x = x0 + slot * i + (slot - bw) / 2;
      o.push(new Rect({ left: x, top, width: bw, height: Math.max(2, y1 - top), rx: 6, ry: 6, fill: color(0) }));
      o.push(text(fmtNum(v), { left: x - 10, top: top - 28, width: bw + 20, fontSize: 18, fontWeight: 600, textAlign: 'center' }));
    });
  }
  return group(o, 'Chart', { chart: spec });
}

// ================================================================ tables

export type TableStyle = 'brand' | 'dark' | 'minimal';

export interface TableSpec {
  rows: string[][];
  header: boolean;
  style: TableStyle;
  color: string;
}

export const TABLE_STYLES: ReadonlyArray<readonly [TableStyle, string]> = [
  ['brand', 'Colour'], ['dark', 'Dark'], ['minimal', 'Minimal'],
];

export const sampleTable = (color = ELEMENT_COLORS[0]): TableSpec => ({
  rows: [
    ['Plan', 'Price', 'Messages'],
    ['Starter', '₹499', '1,000'],
    ['Growth', '₹1,499', '5,000'],
    ['Pro', '₹3,999', '20,000'],
  ],
  header: true,
  style: 'brand',
  color,
});

/** One row per line; cells split by tab, "|" or comma (at most 14 rows × 8 columns). */
export function parseTable(src: string): string[][] {
  const lines = src.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).slice(0, 14);
  return lines.map((l) => {
    const sep = l.includes('\t') ? '\t' : l.includes('|') ? '|' : ',';
    return l.split(sep).map((c) => c.trim()).slice(0, 8);
  });
}

export const tableText = (s: TableSpec): string => s.rows.map((r) => r.join(', ')).join('\n');

/** The table as one group (800 wide, 56 per row), carrying its spec in `table`. */
export function makeTable(spec: TableSpec): Group {
  const rows = spec.rows.length ? spec.rows : [['']];
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const W = 800;
  const rowH = 56;
  const H = rows.length * rowH;
  const colW = W / cols;
  const dark = spec.style === 'dark';
  const headFill = spec.style === 'minimal' ? '#ffffff' : dark ? '#0f1f1a' : spec.color;
  const headInk = spec.style === 'minimal' ? INK : '#ffffff';
  const body = dark ? '#16302a' : '#ffffff';
  const stripe = dark ? '#1c3a33' : spec.style === 'minimal' ? '#ffffff' : '#f2f7f4';
  const ink = dark ? '#eaf3ef' : INK;
  const line = dark ? '#2b4a42' : '#d6e0db';
  const o: FabricObject[] = [
    new Rect({ left: 0, top: 0, width: W, height: H, rx: 14, ry: 14, fill: body, stroke: line, strokeWidth: 2 }),
  ];
  rows.forEach((r, ri) => {
    const y = ri * rowH;
    const head = spec.header && ri === 0;
    if (head || ri % 2 === 0) {
      const fill = head ? headFill : stripe;
      // the first and last rows keep the outer rounded corners
      const first = ri === 0;
      const last = ri === rows.length - 1;
      o.push(new Rect({ left: 1, top: y + 1, width: W - 2, height: rowH - 2, rx: first || last ? 12 : 0, ry: first || last ? 12 : 0, fill }));
      if (first && !last) o.push(new Rect({ left: 1, top: y + rowH / 2, width: W - 2, height: rowH / 2 - 1, fill }));
      if (last && !first) o.push(new Rect({ left: 1, top: y + 1, width: W - 2, height: rowH / 2, fill }));
    }
    if (ri > 0) o.push(new Line([0, y, W, y], { stroke: head ? headFill : line, strokeWidth: spec.style === 'minimal' && ri === 1 && spec.header ? 3 : 1.5 }));
    for (let ci = 0; ci < cols; ci++) {
      o.push(text(r[ci] ?? '', {
        left: ci * colW + 18, top: y + (rowH - 22 * 1.16) / 2, width: colW - 30, fontSize: 22,
        fontWeight: head ? 700 : 400, fill: head ? headInk : ink,
      }));
    }
  });
  for (let ci = 1; ci < cols; ci++) {
    o.push(new Line([ci * colW, 0, ci * colW, H], { stroke: line, strokeWidth: 1.5, opacity: spec.style === 'minimal' ? 0 : 1 }));
  }
  return group(o, 'Table', { table: spec });
}

// ================================================================ mockups

export type MockupKind = 'phone' | 'tablet' | 'laptop' | 'browser' | 'polaroid' | 'frame';

export const MOCKUPS: ReadonlyArray<readonly [MockupKind, string]> = [
  ['phone', 'Phone'], ['tablet', 'Tablet'], ['laptop', 'Laptop'], ['browser', 'Browser'], ['polaroid', 'Polaroid'], ['frame', 'Wall frame'],
];

interface MockupParts {
  w: number;
  h: number;
  under: FabricObject[];
  over: FabricObject[];
  screen: { x: number; y: number; w: number; h: number; radius: number };
}

const soft = () => new Shadow({ color: 'rgba(0,0,0,0.28)', blur: 34, offsetY: 14 });

function mockupParts(kind: MockupKind): MockupParts {
  switch (kind) {
    case 'phone':
      return {
        w: 360, h: 740,
        under: [new Rect({ left: 0, top: 0, width: 360, height: 740, rx: 56, ry: 56, fill: '#111827', shadow: soft() })],
        over: [new Rect({ left: 125, top: 26, width: 110, height: 28, rx: 14, ry: 14, fill: '#111827' })],
        screen: { x: 16, y: 16, w: 328, h: 708, radius: 42 },
      };
    case 'tablet':
      return {
        w: 620, h: 840,
        under: [new Rect({ left: 0, top: 0, width: 620, height: 840, rx: 44, ry: 44, fill: '#1f2937', shadow: soft() })],
        over: [new Circle({ left: 304, top: 12, radius: 6, fill: '#374151' })],
        screen: { x: 30, y: 30, w: 560, h: 780, radius: 16 },
      };
    case 'laptop':
      return {
        w: 920, h: 560,
        under: [
          new Rect({ left: 100, top: 0, width: 720, height: 480, rx: 24, ry: 24, fill: '#111827', shadow: soft() }),
          new Rect({ left: 0, top: 478, width: 920, height: 30, rx: 12, ry: 12, fill: '#cbd5e1', shadow: soft() }),
          new Rect({ left: 390, top: 478, width: 140, height: 10, rx: 5, ry: 5, fill: '#94a3b8' }),
        ],
        over: [],
        screen: { x: 122, y: 22, w: 676, h: 436, radius: 6 },
      };
    case 'browser':
      return {
        w: 900, h: 600,
        under: [
          new Rect({ left: 0, top: 0, width: 900, height: 600, rx: 16, ry: 16, fill: '#ffffff', stroke: '#d1d9d5', strokeWidth: 2, shadow: soft() }),
          new Rect({ left: 1, top: 1, width: 898, height: 50, rx: 15, ry: 15, fill: '#eef2f0' }),
          new Rect({ left: 1, top: 26, width: 898, height: 25, fill: '#eef2f0' }),
          new Circle({ left: 20, top: 19, radius: 7, fill: '#ff5f57' }),
          new Circle({ left: 42, top: 19, radius: 7, fill: '#febc2e' }),
          new Circle({ left: 64, top: 19, radius: 7, fill: '#28c840' }),
          new Rect({ left: 110, top: 13, width: 560, height: 26, rx: 13, ry: 13, fill: '#ffffff' }),
        ],
        over: [],
        screen: { x: 1, y: 51, w: 898, h: 548, radius: 0 },
      };
    case 'polaroid':
      return {
        w: 500, h: 600,
        under: [new Rect({ left: 0, top: 0, width: 500, height: 600, fill: '#ffffff', shadow: soft() })],
        over: [text('Memories', {
          left: 30, top: 500, width: 440, fontSize: 46, fontFamily: 'Dancing Script', textAlign: 'center', fill: '#334155',
        })],
        screen: { x: 30, y: 30, w: 440, h: 440, radius: 0 },
      };
    case 'frame':
    default:
      return {
        w: 520, h: 660,
        under: [
          new Rect({ left: 0, top: 0, width: 520, height: 660, fill: '#3b2f2a', shadow: soft() }),
          new Rect({ left: 26, top: 26, width: 468, height: 608, fill: '#fbfaf7' }),
        ],
        over: [],
        screen: { x: 76, y: 76, w: 368, h: 508, radius: 0 },
      };
  }
}

/**
 * A device mockup for a page of W × H: the device, a photo frame on its screen (filled with `image`
 * when given, else a placeholder to drop a photo into) and anything that sits over the screen.
 */
export function makeMockup(kind: MockupKind, W: number, H: number, image?: FabricImage | null, share = 0.62): FabricObject[] {
  const p = mockupParts(kind);
  const k = Math.min((W * share) / p.w, (H * share) / p.h);
  const left = (W - p.w * k) / 2;
  const top = (H - p.h * k) / 2;
  const fit = (objs: FabricObject[], name: string): FabricObject | null => {
    if (!objs.length) return null;
    const g = group(objs, name);
    const gx = g.left;
    const gy = g.top;
    g.set({ scaleX: k, scaleY: k, left: left + gx * k, top: top + gy * k });
    g.setCoords();
    return g;
  };
  const frame: Frame = {
    x: left + p.screen.x * k, y: top + p.screen.y * k, w: p.screen.w * k, h: p.screen.h * k,
    radius: p.screen.radius * k, shape: 'rect',
  };
  const screen: FabricObject = image ? fitImageToFrame(image, frame) : makePlaceholder(frame, 'Photo');
  screen.set({ name: `${MOCKUPS.find((m) => m[0] === kind)?.[1] ?? 'Mockup'} screen` });
  return [fit(p.under, 'Mockup'), screen, fit(p.over, 'Mockup detail')].filter((x): x is FabricObject => !!x);
}

// ================================================================ graphics

export interface GraphicDef {
  key: string;
  label: string;
  make: (color: string) => FabricObject;
}

const STAR_POINTS = (n: number, r: number, r0: number, cx: number, cy: number) =>
  Array.from({ length: n * 2 }, (_, i) => {
    const a = (Math.PI * i) / n - Math.PI / 2;
    const rad = i % 2 ? r0 : r;
    return { x: cx + rad * Math.cos(a), y: cy + rad * Math.sin(a) };
  });

/** Decorative graphics (base size about 300): badges, ribbons, bubbles, doodles. */
export const GRAPHICS: GraphicDef[] = [
  {
    key: 'burst', label: 'Sale burst',
    make: (c) => group([
      new Polygon(STAR_POINTS(18, 150, 124, 150, 150), { fill: c }),
      text('SALE', { left: 40, top: 102, width: 220, fontSize: 70, fontWeight: 800, fill: '#ffffff', textAlign: 'center' }),
    ], 'Sale burst'),
  },
  {
    key: 'ribbon', label: 'Ribbon',
    make: (c) => group([
      new Path('M0 40 H60 V120 H0 L22 80 Z', { fill: c, opacity: 0.7 }),
      new Path('M300 40 H240 V120 H300 L278 80 Z', { fill: c, opacity: 0.7 }),
      new Rect({ left: 30, top: 20, width: 240, height: 80, fill: c }),
      text('NEW', { left: 30, top: 36, width: 240, fontSize: 42, fontWeight: 800, fill: '#ffffff', textAlign: 'center' }),
    ], 'Ribbon'),
  },
  {
    key: 'bubble', label: 'Speech bubble',
    make: (c) => group([
      new Path('M40 0 H260 Q300 0 300 40 V150 Q300 190 260 190 H120 L60 240 L74 190 H40 Q0 190 0 150 V40 Q0 0 40 0 Z', { fill: c }),
      text('Hello!', { left: 20, top: 66, width: 260, fontSize: 48, fontWeight: 700, fill: '#ffffff', textAlign: 'center' }),
    ], 'Speech bubble'),
  },
  {
    key: 'tag', label: 'Price tag',
    make: (c) => group([
      new Path('M0 0 H220 L300 80 L220 160 H0 Z', { fill: c }),
      new Circle({ left: 222, top: 68, radius: 12, fill: '#ffffff' }),
      text('₹499', { left: 10, top: 44, width: 200, fontSize: 56, fontWeight: 800, fill: '#ffffff', textAlign: 'center' }),
    ], 'Price tag'),
  },
  {
    key: 'badge', label: 'Round badge',
    make: (c) => group([
      new Circle({ left: 0, top: 0, radius: 150, fill: c }),
      new Circle({ left: 18, top: 18, radius: 132, fill: '', stroke: '#ffffff', strokeWidth: 4, strokeDashArray: [10, 8] }),
      text('100%\nORIGINAL', { left: 30, top: 92, width: 240, fontSize: 40, fontWeight: 800, lineHeight: 1, fill: '#ffffff', textAlign: 'center' }),
    ], 'Badge'),
  },
  {
    key: 'arrow', label: 'Curved arrow',
    make: (c) => group([
      new Path('M10 220 C 60 60, 200 40, 270 90', { fill: '', stroke: c, strokeWidth: 12, strokeLineCap: 'round' }),
      new Path('M232 52 L282 96 L222 116', { fill: '', stroke: c, strokeWidth: 12, strokeLineCap: 'round', strokeLineJoin: 'round' }),
    ], 'Curved arrow'),
  },
  {
    key: 'underline', label: 'Brush underline',
    make: (c) => new Path('M5 40 C 80 10, 180 10, 295 28 C 200 26, 120 30, 40 52 Z', { fill: c, name: 'Brush underline', id: newId() }),
  },
  {
    key: 'sparkles', label: 'Sparkles',
    make: (c) => {
      const spark = (x: number, y: number, s: number) =>
        new Path(`M${x} ${y - s} Q${x} ${y} ${x + s} ${y} Q${x} ${y} ${x} ${y + s} Q${x} ${y} ${x - s} ${y} Q${x} ${y} ${x} ${y - s} Z`, { fill: c });
      return group([spark(110, 110, 100), spark(240, 60, 48), spark(230, 220, 36)], 'Sparkles');
    },
  },
  {
    key: 'blob', label: 'Blob',
    make: (c) => new Path(
      'M150 10 C 230 0 300 60 290 140 C 280 220 230 300 150 290 C 70 280 0 230 10 150 C 20 70 70 20 150 10 Z',
      { fill: c, name: 'Blob', id: newId() },
    ),
  },
  {
    key: 'wave', label: 'Wave divider',
    make: (c) => new Path('M0 40 C 50 0, 100 0, 150 40 S 250 80, 300 40 V 100 H 0 Z', { fill: c, name: 'Wave', id: newId() }),
  },
  {
    key: 'star', label: 'Star',
    make: (c) => new Polygon(STAR_POINTS(5, 150, 62, 150, 150), { fill: c, name: 'Star', id: newId() }),
  },
  {
    key: 'circle-text', label: 'Offer circle',
    make: (c) => group([
      new Circle({ left: 0, top: 0, radius: 150, fill: '#ffffff', stroke: c, strokeWidth: 10 }),
      text('UP TO', { left: 40, top: 70, width: 220, fontSize: 30, fontWeight: 600, fill: c, textAlign: 'center' }),
      text('50%', { left: 30, top: 104, width: 240, fontSize: 84, fontWeight: 800, fill: c, textAlign: 'center' }),
      text('OFF', { left: 40, top: 196, width: 220, fontSize: 30, fontWeight: 600, fill: c, textAlign: 'center' }),
    ], 'Offer circle'),
  },
];

/** Small previews for the panels, cached per element and colour. */
const previews = new Map<string, Promise<string>>();

export function previewOf(key: string, build: () => FabricObject[] | FabricObject, size = 160): Promise<string> {
  if (!previews.has(key)) {
    previews.set(key, (async () => {
      const objs = build();
      const blob = await renderToBlob(Array.isArray(objs) ? objs : [objs], size);
      return URL.createObjectURL(blob);
    })());
  }
  return previews.get(key)!;
}
